import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  DSButton,
  DSCheckbox,
  DSDropdown,
  DSPagination,
  Table as DSTable,
  TableAlign
} from '@vr/ds-react'

import { Check } from '../operators-list/check'
import { TableActionsDropdown } from '@/compositions/table-actions-dropdown'
import { OPERATOR_LIST_COLUMNS } from '@/constants/operator-list-columns'
import { useGeneralContext } from '@/contexts/general.context'
import { useUnitsList } from '@/hooks/use-units-list'

export const UnitsList = ({ operator }: { operator: any }) => {
  const { setActiveDrawer, unitListRefetchTrigger, selectItems, operator: contextOperator, setOperator, setSelectItems } = useGeneralContext()
  const {
    handleLimitChange: onLimitChange,
    limit,
    refetch,
    results,
    setPage,
    tableData
  } = useUnitsList(operator)

  const [checkedAll, setCheckedAll] = useState(false)

  useEffect(() => {
    if (unitListRefetchTrigger > 0) {
      refetch()
    }
  }, [unitListRefetchTrigger])

  const mappedData = useMemo(() => {
    return tableData.map(item => {
      return {
        ...item
      }
    })
  }, [tableData])

  useEffect(() => {
    const hasAnySelected = mappedData.some(item =>
      selectItems.some((s: any) => s.id === item.id)
    )
    if (!hasAnySelected) {
      setCheckedAll(false)
    }
  }, [selectItems])

  const handleLimitChange = useCallback(
    (selectedOptions: string | number | (string | number)[]) => {
      let value: string | number | undefined
      if (Array.isArray(selectedOptions)) {
        value = selectedOptions[0]
      } else {
        value = selectedOptions
      }
      const newLimit =
        typeof value === 'number' ? value : Number.parseInt(value, 10)
      onLimitChange(newLimit)
    },
    [onLimitChange]
  )

  return (
    <>
      <DSTable
        skeleton={{
          columns: 5,
          rows: 10
        }}
        css={{ width: '100%', marginTop: '20px' }}
      >
        <DSTable.Head>
          <DSTable.Cell colSpan={7} align='right'>
            <DSDropdown
              options={[
                { label: '10' },
                { label: '25' },
                { label: '50' },
                { label: '100' }
              ]}
              onSelectionChange={handleLimitChange}
              css={{ padding: '16px 0' }}
            >
              <DSButton
                variant='secondary'
                size='small'
                iconRight='principal-chevron-down'
              >
                {`Mostrar: ${limit}`}
              </DSButton>
            </DSDropdown>
          </DSTable.Cell>
        </DSTable.Head>
        <DSTable.Head>
          {OPERATOR_LIST_COLUMNS.map(item => (
            <DSTable.Cell
              key={item.field}
              id={item.field}
              align={item.align as TableAlign}
            >
              {item.field === 'radio' ? (
                <DSCheckbox
                  checked={checkedAll}
                  variant='default'
                  name='checkbox-header'
                  onChange={e => {
                    const isChecked = (e.target as HTMLInputElement).checked
                    setCheckedAll(isChecked)
                    if (isChecked) {
                      setActiveDrawer(true)
                      setOperator(operator)
                      if (contextOperator?.idOperadora !== operator?.idOperadora) {
                        setSelectItems(mappedData)
                      } else {
                        setSelectItems(prev => {
                          const sem = prev.filter((s: any) => !mappedData.some((m: any) => m.id === s.id))
                          return [...sem, ...mappedData]
                        })
                      }
                    } else {
                      setSelectItems(prev => prev.filter((s: any) => !mappedData.some((m: any) => m.id === s.id)))
                    }
                  }}
                />
              ) : (
                item.headerName
              )}
            </DSTable.Cell>
          ))}
        </DSTable.Head>
        <DSTable.Body>
          {mappedData.map(item => (
            <DSTable.Row key={item.id} id={item.id?.toString()}>
              {OPERATOR_LIST_COLUMNS.map(key => (
                <DSTable.Cell
                  key={key.field}
                  align={key.align as TableAlign}
                >
                  {key.field === 'radio' ? (
                    <Check
                      item={item}
                      onClick={() => setActiveDrawer(true)}
                      operator={operator}
                    />
                  ) : key.field === 'acoes' ? (
                    <TableActionsDropdown
                      pageResource='general'
                      unit={item}
                      files={item.urlArquivos}
                      id={operator.idOperadora}
                      onRemoveSuccess={refetch}
                    />
                  ) : ['nomeUnidade', 'nome'].includes(key.field) ? (
                    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {item[key.field as keyof typeof item]}
                    </span>
                  ) : (
                    item[key.field as keyof typeof item]
                  )}
                </DSTable.Cell>
              ))}
            </DSTable.Row>
          ))}
        </DSTable.Body>
      </DSTable>

      <DSPagination
        total={results}
        maxPerPage={limit}
        handleChange={(newPage: number) => setPage(newPage)}
        nextPreview
        siblingCount={1}
      />
    </>
  )
}
